import { Radar, Check } from "lucide-react";
import { useLang } from "@/App";
import { Reveal, Overline } from "@/components/motion";
import { IMAGES } from "@/i18n";

const stepIds = ["step-1-pond-setup", "step-2-sensor-telemetry", "step-3-local-distribution", "step-4-oracle-validation"];

export default function HowItWorks() {
  const { t } = useLang();
  return (
    <>
      <section className="bg-bone pb-8 pt-20 sm:pt-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{t.how.hero.overline}</Overline>
            <h1 data-testid="how-hero-title" className="mt-4 max-w-3xl font-headline text-4xl font-extrabold leading-[1.08] tracking-tight text-deep sm:text-5xl">
              {t.how.hero.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg">
              {t.how.hero.sub}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-bone py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="absolute bottom-8 left-5 top-8 w-px bg-teal/20 sm:left-1/2" aria-hidden="true" />
            <div className="space-y-14">
              {t.how.steps.map((s, i) => (
                <Reveal key={i} delay={0.05}>
                  <div
                    data-testid={stepIds[i]}
                    className={`relative flex flex-col gap-6 pl-14 sm:w-1/2 sm:pl-0 ${
                      i % 2 === 0 ? "sm:pr-14 sm:text-right" : "sm:ml-auto sm:pl-14"
                    }`}
                  >
                    <span
                      className={`absolute top-1 flex h-10 w-10 items-center justify-center rounded-full border border-aqua/40 bg-deep font-mono text-xs font-bold text-aqua ${
                        i % 2 === 0 ? "left-0 sm:left-auto sm:-right-5" : "left-0 sm:-left-5"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-teal">{s.k}</p>
                      <h2 className="mt-2 font-headline text-xl font-bold text-deep sm:text-2xl">{s.title}</h2>
                      <p className={`mt-3 max-w-md text-sm leading-relaxed text-ink/65 sm:text-base ${i % 2 === 0 ? "sm:ml-auto" : ""}`}>
                        {s.desc}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-bone pb-20 sm:pb-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div data-testid="sensor-tech-card" className="grain-overlay relative overflow-hidden rounded-3xl bg-deep">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative p-8 sm:p-12">
                  <div className="absolute -left-16 -top-16 h-48 w-48 rounded-full bg-aqua/10 blur-3xl" aria-hidden="true" />
                  <Overline dark>{t.how.sensor.overline}</Overline>
                  <h2 className="mt-4 font-headline text-2xl font-bold tracking-tight text-bone sm:text-3xl">
                    {t.how.sensor.title}
                  </h2>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-bone/70 sm:text-base">
                    {t.how.sensor.desc}
                  </p>
                  <ul className="mt-8 space-y-3.5">
                    {t.how.sensor.points.map((p, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-bone/85">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-aqua" />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <Radar className="mt-10 h-8 w-8 text-aqua/50" />
                </div>
                <div className="clipped-frame relative min-h-[18rem]">
                  <img
                    src={IMAGES.coastal_buoy_sensors}
                    alt={t.how.sensor.title}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
              </div>
            </div>
          </Reveal>

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
            {[IMAGES.shrimp_larvae_inspection, IMAGES.shrimp_culture_sample].map((src, i) => (
              <Reveal key={i} delay={0.08 * i}>
                <figure className="group overflow-hidden rounded-3xl">
                  <img
                    src={src}
                    alt={t.how.galleryCaptions[i]}
                    className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-80"
                  />
                  <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-ink/50">
                    {t.how.galleryCaptions[i]}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
